import { useCallback, useEffect, useRef } from 'react';
import { createFrameCoalescer } from './frame_coalescer';

/**
 * React hook wrapper around createFrameCoalescer, for a callback that comes
 * from props/state and can change across renders (e.g. RangeSlider's
 * onValueChange). `send`/`flush` stay referentially stable across renders —
 * long-lived JSX handlers (onChange, onPointerUp, ...) don't need to be
 * re-bound — while always calling the latest callback. Any pending frame is
 * cancelled on unmount.
 *
 * Callers that create their own gesture-scoped coalescer inside an imperative
 * event-handler closure (useLineDrag, useDragReorder) use
 * `createFrameCoalescer` directly instead: a hook can't be called there.
 */
export function useFrameThrottled<Args extends unknown[]>(
   callback: (...args: Args) => void
): { send: (...args: Args) => void; flush: () => void } {
   const callbackRef = useRef(callback);
   useEffect(() => {
      callbackRef.current = callback;
   });

   // Created in an effect, not during render: the coalescer closes over
   // callbackRef, and reading a ref belongs outside render. send/flush only
   // fire from mounted event handlers, so the coalescer always exists by then.
   const coalescerRef = useRef<ReturnType<typeof createFrameCoalescer<Args>> | null>(null);
   useEffect(() => {
      const coalescer = createFrameCoalescer<Args>((...args) => callbackRef.current(...args));
      coalescerRef.current = coalescer;
      return () => {
         coalescer.cancel();
         coalescerRef.current = null;
      };
   }, []);

   const send = useCallback((...args: Args) => coalescerRef.current?.send(...args), []);
   const flush = useCallback(() => coalescerRef.current?.flush(), []);

   return { send, flush };
}
